export const DEMO_ACCESS_CODE = 'AccessInfo@Advoq2026!';
export const DEMO_ACCESS_STORAGE_KEY = 'advoq_corporate_ma_demo_unlocked_v1';

export const DEFAULT_DEMO_ROUTE = '/demo/matters';
export const DEFAULT_DEMO_SCOPE_ID = 'personal';
export const DEFAULT_DEMO_MATTER_ID = 'project-kaveri';
export const DEFAULT_DEMO_SECTION_ID = 'overview';

export const DEMO_SCOPE_OPTIONS = [
  {
    id: 'personal',
    label: 'Personal',
    shortLabel: 'Personal',
  },
  {
    id: 'team',
    label: 'Marbridge Team',
    shortLabel: 'Team',
  },
];

export const DEMO_WORKSPACE_SECTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'draft', label: 'Draft' },
  { id: 'files', label: 'Files' },
  { id: 'jolly', label: 'Jolly' },
  { id: 'review', label: 'Review' },
];

const VALID_SECTION_IDS = new Set(DEMO_WORKSPACE_SECTIONS.map((section) => section.id));

export function buildDemoMatterPath(matterId, sectionId = DEFAULT_DEMO_SECTION_ID) {
  return `/demo/matters/${matterId}/${sectionId}`;
}

export const CORPORATE_MA_DEMO = {
  meta: {
    title: 'Advoq customer demo',
    projectName: 'Project Kaveri',
    subtitle: 'Marbridge Growth Partners investment into VedaLedger Technologies Private Limited',
    workspaceLabel: 'Marbridge Team',
    entrySummary: 'Matter-first M&A workspace with structured review, voice capture, and export-ready outputs.',
  },
  scopes: [
    {
      id: 'personal',
      label: 'Personal',
      resumeMatterId: 'project-kaveri',
      matterIds: ['project-kaveri', 'project-narmada', 'project-orchid'],
      recentWork: [
        {
          matterId: 'project-kaveri',
          title: 'Client note tightened',
          detail: 'Partner version now leads with the three execution items.',
          time: '18 min ago',
        },
        {
          matterId: 'project-narmada',
          title: 'Disclosure tracker refreshed',
          detail: 'Seller comments folded into the SHA board pack.',
          time: '1 hr ago',
        },
        {
          matterId: 'project-orchid',
          title: 'Signing checklist reopened',
          detail: 'Board consent sequence moved back to today.',
          time: '2 hrs ago',
        },
      ],
      draftsInProgress: [
        {
          matterId: 'project-kaveri',
          title: 'Client update',
          status: 'Ready for review',
          updated: 'Today 6:10 PM',
        },
        {
          matterId: 'project-narmada',
          title: 'Board memo',
          status: 'In draft',
          updated: 'Today 5:25 PM',
        },
        {
          matterId: 'project-orchid',
          title: 'CP tracker',
          status: 'Needs owner',
          updated: 'Today 4:40 PM',
        },
      ],
      recentExports: [
        {
          matterId: 'project-kaveri',
          title: 'Partner note PDF',
          format: 'PDF',
          updated: 'Today 6:12 PM',
        },
        {
          matterId: 'project-narmada',
          title: 'Issue list XLSX',
          format: 'XLSX',
          updated: 'Yesterday',
        },
      ],
    },
    {
      id: 'team',
      label: 'Marbridge Team',
      resumeMatterId: 'project-kaveri',
      matterIds: ['project-kaveri', 'project-lotus', 'project-orchid'],
      recentWork: [
        {
          matterId: 'project-kaveri',
          title: 'Consent tracker updated',
          detail: 'Commercial owner names are now assigned.',
          time: '9 min ago',
          owner: 'Naman Sethi',
        },
        {
          matterId: 'project-lotus',
          title: 'Funds-flow markup saved',
          detail: 'Tax holdback fallback moved into the clean version.',
          time: '34 min ago',
          owner: 'Ritika Shah',
        },
        {
          matterId: 'project-orchid',
          title: 'Employment annexure reviewed',
          detail: 'Retention economics cleared for partner sign-off.',
          time: '52 min ago',
          owner: 'Kabir Mehta',
        },
      ],
      draftsInProgress: [
        {
          matterId: 'project-kaveri',
          title: 'Client update',
          status: 'Ready for partner review',
          updated: 'Today 6:10 PM',
        },
        {
          matterId: 'project-lotus',
          title: 'Funds-flow memo',
          status: 'In draft',
          updated: 'Today 5:48 PM',
        },
        {
          matterId: 'project-orchid',
          title: 'Signing pack cover note',
          status: 'Queued',
          updated: 'Today 3:20 PM',
        },
      ],
      recentExports: [
        {
          matterId: 'project-kaveri',
          title: 'Partner note PDF',
          format: 'PDF',
          updated: 'Today 6:12 PM',
        },
        {
          matterId: 'project-lotus',
          title: 'Closing checklist XLSX',
          format: 'XLSX',
          updated: 'Today 2:05 PM',
        },
        {
          matterId: 'project-orchid',
          title: 'Signature packet ZIP',
          format: 'ZIP',
          updated: 'Yesterday',
        },
      ],
    },
  ],
  matters: [
    {
      id: 'project-kaveri',
      name: 'Project Kaveri',
      client: 'Marbridge Growth Partners',
      counterparty: 'VedaLedger Technologies Private Limited',
      matterType: 'Growth investment',
      status: 'Active',
      stage: 'Confirmatory diligence',
      owner: 'Priya Mehra',
      team: 'Marbridge Team',
      nextDateLabel: 'Signing target',
      nextDate: '28 Apr 2026',
      lastTouch: '18 min ago',
      fileCount: '48 files',
      summary: 'Primary plus secondary under SSA, SPA, and SHA with the blocker list narrowed to three items.',
      resumeLabel: 'Resume Project Kaveri',
      workspace: {
        resume: {
          currentPosition:
            'The matter is no longer about broad review. It is about keeping the blocker list short and converting the rest into drafting and closing discipline.',
          nextSteps: [
            'Send the one-page client note before the 8:30 PM call.',
            'Confirm owner and outreach timing for the two bank consents.',
            'Keep the SSA fallback on data indemnity ready if IP deeds slip.',
          ],
          keyFacts: [
            { label: 'Client', value: 'Marbridge Growth Partners' },
            { label: 'Target', value: 'VedaLedger Technologies' },
            { label: 'Value', value: 'INR 335Cr' },
            { label: 'Structure', value: 'Primary + secondary' },
          ],
          latestActivity: [
            'Partner note tightened for tonight’s client call.',
            'Consent tracker moved into named-owner format.',
            'SSA fallback saved as an export-ready draft.',
          ],
        },
        overview: {
          summary: 'Execution risk is contained if the team keeps customer consents, contractor IP deeds, and ESOP clean-up in view.',
          watchItems: [
            {
              title: 'Customer consents',
              detail: 'Axis Allied Bank and Saral Finance still need written consent before indirect change of control.',
              tone: 'Blocked',
              meta: 'Closing',
            },
            {
              title: 'Contractor IP deeds',
              detail: 'Two early contractor assignment deeds are still outstanding for core product modules.',
              tone: 'Watch',
              meta: 'Signing',
            },
            {
              title: 'ESOP pool clean-up',
              detail: '0.7% sits outside the approved pool and needs pre-signing alignment.',
              tone: 'Action needed',
              meta: 'Signing',
            },
          ],
          timeline: [
            {
              date: 'Tonight 8:30 PM',
              label: 'Client call',
              detail: 'Send one-page note and short consent update first.',
            },
            {
              date: '14 Apr 2026',
              label: 'Consent outreach',
              detail: 'Release letters once business owners sign off.',
            },
            {
              date: '20 Apr 2026',
              label: 'Economics freeze',
              detail: 'Lock ESOP and indemnity structure into the signing set.',
            },
          ],
          synthesis: {
            label: 'Matter synthesis',
            caption: 'Powered by Sabha',
            sections: [
              {
                title: 'Summary',
                items: [
                  'Three items still deserve active partner attention.',
                  'Everything else has already moved into drafting, disclosure, or closing actions.',
                ],
              },
              {
                title: 'Key facts',
                items: [
                  'No broad transaction failure story is developing.',
                  'The clean negotiation leverage remains data indemnity, IP deeds, and ESOP economics.',
                ],
              },
              {
                title: 'Next decisions',
                items: [
                  'Keep only the two named bank consents as hard closing conditions.',
                  'Use a holdback fallback if the IP deeds are not signed before packet circulation.',
                ],
              },
            ],
          },
        },
        draft: {
          current: {
            title: 'Client update',
            status: 'Ready for review',
            updated: 'Today 6:10 PM',
          },
          sections: [
            {
              title: 'Summary',
              text: 'Project Kaveri remains on track for signing on 28 April 2026. The live attention list is down to customer consents, contractor IP deeds, and ESOP alignment.',
            },
            {
              title: 'Key points',
              items: [
                'Only the two named bank consents should be treated as hard closing items.',
                'Contractor IP deeds justify a specific fallback if execution slips.',
                'The remaining diligence has already been translated into drafting asks or dated closing work.',
              ],
            },
            {
              title: 'Export set',
              items: [
                'Partner note PDF',
                'Consent tracker XLSX',
                'SSA fallback redline DOCX',
              ],
            },
          ],
          negotiations: [
            {
              clause: 'Data indemnity',
              lead: 'Keep a separate cap tied to the incident disclosure gap and regulated-customer profile.',
              fallback: 'Accept tighter language only if the IP deed package lands before signature packets go out.',
            },
            {
              clause: 'Customer consent covenant',
              lead: 'Make only the two named bank contracts hard closing conditions.',
              fallback: 'Push the remaining population into a disclosure-backed transition covenant.',
            },
            {
              clause: 'ESOP refresh',
              lead: 'Do not let undocumented promises dilute the investor.',
              fallback: 'Separate approved pool economics from unapproved awards in the funds-flow note.',
            },
          ],
        },
        files: {
          folders: [
            { name: 'Corporate', count: '11', note: 'Cap table and ESOP' },
            { name: 'Commercial', count: '9', note: 'Customer consents' },
            { name: 'Tech / IP', count: '12', note: 'Assignments and schedules' },
            { name: 'Regulatory', count: '8', note: 'FEMA and filings' },
          ],
          items: [
            {
              id: 'kaveri-customers',
              name: 'Top 12 customer MSAs bundle',
              label: 'Commercial',
              meta: '314 pages',
              updated: '6 Apr 2026',
              summary: 'Two consent requirements and renewal concentration are already extracted.',
              highlights: [
                'Axis Allied Bank and Saral Finance require prior written consent.',
                'Three regulated customers still rely on older security schedules.',
                '38% of ARR renews in the six months after closing.',
              ],
              missing: [
                'Final consent template for Axis Allied Bank.',
                'Commercial owner note for Saral Finance outreach.',
              ],
            },
            {
              id: 'kaveri-esop',
              name: 'Cap table and ESOP roll-forward',
              label: 'Corporate',
              meta: '26 pages',
              updated: '6 Apr 2026',
              summary: 'The approved pool and promised awards still do not match.',
              highlights: [
                'Approved pool is 8.0%; the current diluted model assumes 8.7%.',
                'Seven 2024 grants are referenced but not fully backed by board minutes.',
              ],
              missing: [
                'Final board minutes for July 2024 grants.',
                'Shareholder ratification path for off-plan awards.',
              ],
            },
            {
              id: 'kaveri-ip',
              name: 'IP assignment and OSS memo',
              label: 'Tech / IP',
              meta: '61 pages',
              updated: '6 Apr 2026',
              summary: 'The deed gap is narrow but still real for two early contractors.',
              highlights: [
                'Two core module contractors still need executed assignment deeds.',
                'No active copyleft contamination appears from the current memo.',
              ],
              missing: [
                'Executed deed for Rahul Kulkarni.',
                'Executed deed for Axiom Logic LLP.',
              ],
            },
          ],
        },
        jolly: {
          captures: [
            {
              id: 'kaveri-jolly-partner',
              title: 'Partner sync',
              time: 'Today 5:42 PM',
              duration: '14 min',
              summary: 'Partner wants a client-safe note tonight and a narrow fallback on consent timing.',
              noteSections: [
                {
                  title: 'Summary',
                  items: [
                    'Lead with the short blocker list, not the full diligence spread.',
                    'Keep the client note calm and execution-focused.',
                  ],
                },
                {
                  title: 'Actions',
                  items: [
                    'Export the updated client note before the 8:30 PM call.',
                    'Confirm who owns each bank consent before sending the outreach pack.',
                  ],
                },
              ],
            },
            {
              id: 'kaveri-jolly-business',
              title: 'Business owner check-in',
              time: 'Today 3:18 PM',
              duration: '9 min',
              summary: 'Commercial team confirmed both named bank consents are genuine closing items.',
              noteSections: [
                {
                  title: 'Key facts',
                  items: [
                    'No broader customer consent sweep is needed.',
                    'Business owners prefer a narrow CP set.',
                  ],
                },
              ],
            },
          ],
        },
        review: {
          caption: 'Powered by Aina',
          findings: [
            {
              id: 'kaveri-review-note',
              title: 'Client note still buries the consent issue',
              severity: 'High',
              summary: 'Lead with the two named bank consents as a closing item instead of placing them mid-note with general diligence points.',
              risks: [
                'The client may underweight the only real closing dependency.',
                'The note reads broader than the actual risk picture.',
              ],
              fixes: [
                'Move the consent line into the opening summary.',
                'Separate closing items from paperable drafting points.',
              ],
              references: ['Client update', 'Top 12 customer MSAs bundle'],
            },
            {
              id: 'kaveri-review-fallback',
              title: 'SSA fallback needs a cleaner IP deed trigger',
              severity: 'Medium',
              summary: 'Tie the indemnity fallback directly to deed delivery timing so the commercial ask stays precise.',
              risks: [
                'Loose fallback drafting weakens leverage in the next markup.',
              ],
              fixes: [
                'Name the two deed gaps in the fallback note.',
                'Keep the holdback alternative next to the indemnity ask.',
              ],
              references: ['SSA fallback redline', 'IP assignment and OSS memo'],
            },
          ],
        },
        outputs: [
          {
            title: 'Partner note',
            label: 'Email draft',
            status: 'Ready',
            updated: 'Today 6:10 PM',
            summary: 'One-page client update with the short blocker view.',
            actionLabel: 'Export PDF',
          },
          {
            title: 'Consent tracker',
            label: 'Checklist',
            status: 'In progress',
            updated: 'Today 5:58 PM',
            summary: 'Named-owner path for each bank consent and evidence item.',
            actionLabel: 'Export XLSX',
          },
          {
            title: 'SSA fallback',
            label: 'Redline note',
            status: 'Ready',
            updated: 'Today 5:24 PM',
            summary: 'Fallback on data indemnity and deed delivery if the close path tightens.',
            actionLabel: 'Export DOCX',
          },
        ],
      },
    },
    {
      id: 'project-narmada',
      name: 'Project Narmada',
      client: 'AsterGrid Capital',
      counterparty: 'NovaSwitch Energy Private Limited',
      matterType: 'Secondary acquisition',
      status: 'Active',
      stage: 'Board pack',
      owner: 'Priya Mehra',
      team: 'Personal',
      nextDateLabel: 'Board note',
      nextDate: '09 Apr 2026',
      lastTouch: '1 hr ago',
      fileCount: '22 files',
      summary: 'Board memo and disclosure tracker are moving into sign-off.',
      resumeLabel: 'Open Project Narmada',
      workspace: {
        resume: {
          currentPosition:
            'This file is in packaging mode. The key work is keeping the board memo tight and the disclosure tracker current.',
          nextSteps: [
            'Finalize the board memo summary for tomorrow morning.',
            'Confirm the seller-side tax wording in the clean SHA.',
            'Export the disclosure tracker after partner sign-off.',
          ],
          keyFacts: [
            { label: 'Client', value: 'AsterGrid Capital' },
            { label: 'Target', value: 'NovaSwitch Energy' },
            { label: 'Value', value: 'INR 142Cr' },
            { label: 'Stage', value: 'Board pack' },
          ],
          latestActivity: [
            'Disclosure tracker refreshed with seller comments.',
            'Board memo fallback language shortened.',
          ],
        },
        overview: {
          summary: 'The matter is stable. The only open attention is disclosure precision and board-facing framing.',
          watchItems: [
            {
              title: 'Tax wording',
              detail: 'Seller-side withholding language needs a cleaner board-facing explanation.',
              tone: 'Watch',
              meta: 'Board memo',
            },
            {
              title: 'Disclosure tracker',
              detail: 'One cyber incident description still needs dates and closure language.',
              tone: 'Action needed',
              meta: 'Disclosure',
            },
          ],
          timeline: [
            {
              date: 'Tomorrow 9:00 AM',
              label: 'Board pack sign-off',
              detail: 'Partner to approve short memo and disclosure tracker.',
            },
          ],
          synthesis: {
            label: 'Matter synthesis',
            caption: 'Powered by Sabha',
            sections: [
              {
                title: 'Summary',
                items: [
                  'No broad execution risk remains.',
                  'The board pack should stay short and disclosure-led.',
                ],
              },
            ],
          },
        },
        draft: {
          current: {
            title: 'Board memo',
            status: 'In draft',
            updated: 'Today 5:25 PM',
          },
          sections: [
            {
              title: 'Summary',
              text: 'Board note is nearly final. Remaining edits are presentation and disclosure calibration rather than commercial re-trade.',
            },
            {
              title: 'Key points',
              items: [
                'Keep tax wording precise and non-alarmist.',
                'Use the disclosure tracker as the working appendix.',
              ],
            },
          ],
          negotiations: [
            {
              clause: 'Tax covenant',
              lead: 'Keep gross-up and withholding language consistent with the funds-flow model.',
              fallback: 'Escalate only if the seller asks for a late-stage shift in allocation.',
            },
          ],
        },
        files: {
          folders: [
            { name: 'Board', count: '4', note: 'Pack and approvals' },
            { name: 'Disclosure', count: '7', note: 'Schedules and trackers' },
          ],
          items: [
            {
              id: 'narmada-board-pack',
              name: 'Board pack v6',
              label: 'Board',
              meta: '18 pages',
              updated: '7 Apr 2026',
              summary: 'Short memo ready for partner clean-up.',
              highlights: [
                'Commercial story is stable.',
                'Open points are now presentation-driven.',
              ],
              missing: [
                'Final sign-off line on withholding wording.',
              ],
            },
          ],
        },
        jolly: {
          captures: [
            {
              id: 'narmada-jolly',
              title: 'Board memo dictation',
              time: 'Today 4:12 PM',
              duration: '7 min',
              summary: 'Dictation converted into a shorter board-facing summary.',
              noteSections: [
                {
                  title: 'Actions',
                  items: [
                    'Export the memo once the tax sentence is trimmed.',
                  ],
                },
              ],
            },
          ],
        },
        review: {
          caption: 'Powered by Aina',
          findings: [
            {
              id: 'narmada-review',
              title: 'Board memo still over-explains the tax point',
              severity: 'Medium',
              summary: 'Keep the board note practical; move the legal detail back into the appendix.',
              risks: ['Board readers may over-index on a manageable tax issue.'],
              fixes: ['Trim the opening paragraph and keep the appendix precise.'],
              references: ['Board memo', 'Disclosure tracker'],
            },
          ],
        },
        outputs: [
          {
            title: 'Board memo',
            label: 'Memo',
            status: 'In draft',
            updated: 'Today 5:25 PM',
            summary: 'Board-facing summary for tomorrow morning.',
            actionLabel: 'Export DOCX',
          },
          {
            title: 'Disclosure tracker',
            label: 'Tracker',
            status: 'Ready',
            updated: 'Today 4:58 PM',
            summary: 'Clean appendix for final board circulation.',
            actionLabel: 'Export XLSX',
          },
        ],
      },
    },
    {
      id: 'project-lotus',
      name: 'Project Lotus',
      client: 'Marbridge Growth Partners',
      counterparty: 'Suryanet Mobility Solutions Private Limited',
      matterType: 'Acquisition close',
      status: 'Active',
      stage: 'Signing set',
      owner: 'Ritika Shah',
      team: 'Marbridge Team',
      nextDateLabel: 'Packet out',
      nextDate: '10 Apr 2026',
      lastTouch: '34 min ago',
      fileCount: '31 files',
      summary: 'Funds-flow, approvals, and signature packet are moving together in one close file.',
      resumeLabel: 'Open Project Lotus',
      workspace: {
        resume: {
          currentPosition:
            'The signing set is nearly ready. The work now is sequence discipline and funds-flow certainty.',
          nextSteps: [
            'Freeze the clean funds-flow memo.',
            'Confirm signatory availability for Friday packet circulation.',
          ],
          keyFacts: [
            { label: 'Client', value: 'Marbridge Growth Partners' },
            { label: 'Target', value: 'Suryanet Mobility' },
            { label: 'Value', value: 'INR 188Cr' },
            { label: 'Stage', value: 'Signing set' },
          ],
          latestActivity: [
            'Funds-flow markup saved for review.',
            'Signature packet cover note queued.',
          ],
        },
        overview: {
          summary: 'This file is execution-led. The remaining work is timing, approvals, and packet control.',
          watchItems: [
            {
              title: 'Funds-flow certainty',
              detail: 'The holdback fallback is ready but not yet confirmed in the clean draft.',
              tone: 'Watch',
              meta: 'Signing',
            },
            {
              title: 'Signatory path',
              detail: 'Two board signatories still need calendar confirmation.',
              tone: 'Action needed',
              meta: 'Packet',
            },
          ],
          timeline: [
            {
              date: '10 Apr 2026',
              label: 'Packet circulation',
              detail: 'Freeze the clean set and release signatory instructions.',
            },
          ],
          synthesis: {
            label: 'Matter synthesis',
            caption: 'Powered by Sabha',
            sections: [
              {
                title: 'Summary',
                items: [
                  'No new legal issue is driving this file.',
                  'The remaining risk is sequencing rather than substance.',
                ],
              },
            ],
          },
        },
        draft: {
          current: {
            title: 'Funds-flow memo',
            status: 'In draft',
            updated: 'Today 5:48 PM',
          },
          sections: [
            {
              title: 'Summary',
              text: 'Funds-flow is close to final. The remaining question is whether the holdback fallback should stay in the clean note.',
            },
          ],
          negotiations: [
            {
              clause: 'Tax holdback',
              lead: 'Keep the fallback in the clean note until seller confirmation lands.',
              fallback: 'Strip it only once the payment evidence pack is complete.',
            },
          ],
        },
        files: {
          folders: [
            { name: 'Signing', count: '12', note: 'Packet and approvals' },
            { name: 'Funds flow', count: '6', note: 'Payment mechanics' },
          ],
          items: [
            {
              id: 'lotus-funds',
              name: 'Funds-flow memo v4',
              label: 'Funds flow',
              meta: '9 pages',
              updated: '7 Apr 2026',
              summary: 'Ready for partner review with one holdback fallback still open.',
              highlights: [
                'Payment path is now fully itemized.',
              ],
              missing: [
                'Seller confirmation on fallback removal.',
              ],
            },
          ],
        },
        jolly: {
          captures: [
            {
              id: 'lotus-jolly',
              title: 'Signing packet call',
              time: 'Today 2:48 PM',
              duration: '6 min',
              summary: 'Sequence the packet only after signatory confirmation.',
              noteSections: [
                {
                  title: 'Actions',
                  items: [
                    'Hold circulation until both board signatories confirm.',
                  ],
                },
              ],
            },
          ],
        },
        review: {
          caption: 'Powered by Aina',
          findings: [
            {
              id: 'lotus-review',
              title: 'Packet cover note is missing signatory timing',
              severity: 'Medium',
              summary: 'Add the signatory sequence so the packet reads like an execution file, not just a document list.',
              risks: ['The packet may be forwarded without the right timing context.'],
              fixes: ['Add one timing block and named owners.'],
              references: ['Packet cover note'],
            },
          ],
        },
        outputs: [
          {
            title: 'Funds-flow memo',
            label: 'Memo',
            status: 'In progress',
            updated: 'Today 5:48 PM',
            summary: 'Clean funds-flow note for packet circulation.',
            actionLabel: 'Export DOCX',
          },
          {
            title: 'Closing checklist',
            label: 'Checklist',
            status: 'Ready',
            updated: 'Today 2:05 PM',
            summary: 'Owner-led execution checklist for the signing run.',
            actionLabel: 'Export XLSX',
          },
        ],
      },
    },
    {
      id: 'project-orchid',
      name: 'Project Orchid',
      client: 'Marbridge Growth Partners',
      counterparty: 'Aurum Commerce Platforms Private Limited',
      matterType: 'Founder rollover',
      status: 'Watch',
      stage: 'Employment package',
      owner: 'Kabir Mehta',
      team: 'Marbridge Team',
      nextDateLabel: 'Employment note',
      nextDate: '11 Apr 2026',
      lastTouch: '2 hrs ago',
      fileCount: '17 files',
      summary: 'The matter is focused on founder service terms and retention economics.',
      resumeLabel: 'Open Project Orchid',
      workspace: {
        resume: {
          currentPosition:
            'This file is narrow and negotiation-led. The team needs a realistic India-law service package, not a longer paper trail.',
          nextSteps: [
            'Keep the service-period note short.',
            'Lock the retention economics before the employment annexure moves.',
          ],
          keyFacts: [
            { label: 'Client', value: 'Marbridge Growth Partners' },
            { label: 'Target', value: 'Aurum Commerce' },
            { label: 'Value', value: 'INR 96Cr' },
            { label: 'Stage', value: 'Employment package' },
          ],
          latestActivity: [
            'Retention economics cleared for partner sign-off.',
          ],
        },
        overview: {
          summary: 'The issue here is not volume. It is getting the founder package commercially tough and locally realistic.',
          watchItems: [
            {
              title: 'Service period',
              detail: 'Founder exit flexibility still needs a cleaner commercial fallback.',
              tone: 'Watch',
              meta: 'Employment',
            },
          ],
          timeline: [
            {
              date: '11 Apr 2026',
              label: 'Employment note',
              detail: 'Circulate the final package after economics are confirmed.',
            },
          ],
          synthesis: {
            label: 'Matter synthesis',
            caption: 'Powered by Sabha',
            sections: [
              {
                title: 'Summary',
                items: [
                  'Keep the negotiation on service economics, vesting, and bad-leaver design.',
                ],
              },
            ],
          },
        },
        draft: {
          current: {
            title: 'Employment annexure',
            status: 'Queued',
            updated: 'Today 3:20 PM',
          },
          sections: [
            {
              title: 'Summary',
              text: 'The next draft should stay commercially firm without leaning on unenforceable language.',
            },
          ],
          negotiations: [
            {
              clause: 'Founder service package',
              lead: 'Anchor on service period, vesting, and bad-leaver economics.',
              fallback: 'Do not reopen broad non-compete language if the economics are acceptable.',
            },
          ],
        },
        files: {
          folders: [
            { name: 'Employment', count: '8', note: 'Founder suite' },
          ],
          items: [
            {
              id: 'orchid-employment',
              name: 'Founder employment suite',
              label: 'Employment',
              meta: '23 pages',
              updated: '7 Apr 2026',
              summary: 'Commercial terms are close; only the service package still needs alignment.',
              highlights: [
                'Non-solicit is settled.',
                'Bad-leaver economics are almost locked.',
              ],
              missing: [
                'Final sign-off on service period.',
              ],
            },
          ],
        },
        jolly: {
          captures: [
            {
              id: 'orchid-jolly',
              title: 'Founder package call',
              time: 'Today 1:10 PM',
              duration: '8 min',
              summary: 'Keep the package grounded in service economics, not theory.',
              noteSections: [
                {
                  title: 'Actions',
                  items: [
                    'Trim the note before sending it for partner sign-off.',
                  ],
                },
              ],
            },
          ],
        },
        review: {
          caption: 'Powered by Aina',
          findings: [
            {
              id: 'orchid-review',
              title: 'Employment note still sounds broader than the actual issue',
              severity: 'Low',
              summary: 'Frame the file as a service-package negotiation, not a general employment risk story.',
              risks: ['The client may think the employment work is less settled than it is.'],
              fixes: ['Cut the overview paragraph to one short block.'],
              references: ['Employment note'],
            },
          ],
        },
        outputs: [
          {
            title: 'Employment note',
            label: 'Memo',
            status: 'Queued',
            updated: 'Today 3:20 PM',
            summary: 'Short commercial note on founder service terms.',
            actionLabel: 'Export DOCX',
          },
        ],
      },
    },
  ],
};

const DEMO_SCOPE_BY_ID = new Map(CORPORATE_MA_DEMO.scopes.map((scope) => [scope.id, scope]));
const DEMO_MATTER_BY_ID = new Map(CORPORATE_MA_DEMO.matters.map((matter) => [matter.id, matter]));

const ROUTE_ALIASES = {
  '/demo': DEFAULT_DEMO_ROUTE,
  '/demo/dashboard': DEFAULT_DEMO_ROUTE,
  '/demo/war-room': DEFAULT_DEMO_ROUTE,
  '/demo/diligence-room': buildDemoMatterPath(DEFAULT_DEMO_MATTER_ID, 'files'),
  '/demo/issue-list': buildDemoMatterPath(DEFAULT_DEMO_MATTER_ID, 'review'),
  '/demo/risk-register': buildDemoMatterPath(DEFAULT_DEMO_MATTER_ID, 'review'),
  '/demo/aina': buildDemoMatterPath(DEFAULT_DEMO_MATTER_ID, 'review'),
  '/demo/drafting-negotiation': buildDemoMatterPath(DEFAULT_DEMO_MATTER_ID, 'draft'),
  '/demo/client-reporting': buildDemoMatterPath(DEFAULT_DEMO_MATTER_ID, 'draft'),
  '/demo/closing-tracker': buildDemoMatterPath(DEFAULT_DEMO_MATTER_ID, 'overview'),
  '/demo/team-workspace': buildDemoMatterPath(DEFAULT_DEMO_MATTER_ID, 'overview'),
  '/demo/jolly': buildDemoMatterPath(DEFAULT_DEMO_MATTER_ID, 'jolly'),
  '/demo/knowledge-authorities': buildDemoMatterPath(DEFAULT_DEMO_MATTER_ID, 'files'),
  '/demo/research': buildDemoMatterPath(DEFAULT_DEMO_MATTER_ID, 'files'),
  '/demo/ask-advoq': buildDemoMatterPath(DEFAULT_DEMO_MATTER_ID, 'overview'),
};

function normalizePathname(pathname) {
  const normalized = String(pathname || '').replace(/\/+$/, '');
  return normalized || '/demo';
}

export function isValidDemoAccessCode(value) {
  return String(value || '').trim() === DEMO_ACCESS_CODE;
}

export function getDemoScopeById(scopeId) {
  return DEMO_SCOPE_BY_ID.get(scopeId) || DEMO_SCOPE_BY_ID.get(DEFAULT_DEMO_SCOPE_ID);
}

export function getDemoMatterById(matterId) {
  return DEMO_MATTER_BY_ID.get(matterId) || DEMO_MATTER_BY_ID.get(DEFAULT_DEMO_MATTER_ID);
}

export function getMattersForScope(scopeId) {
  return getDemoScopeById(scopeId).matterIds.map((matterId) => getDemoMatterById(matterId));
}

export function isMatterVisibleInScope(matterId, scopeId) {
  return getDemoScopeById(scopeId).matterIds.includes(matterId);
}

export function getDemoHomeState(scopeId) {
  const scope = getDemoScopeById(scopeId);
  return {
    scope,
    resumeMatter: getDemoMatterById(scope.resumeMatterId),
    matters: scope.matterIds.map((matterId) => getDemoMatterById(matterId)),
    recentWork: scope.recentWork.map((entry) => ({
      ...entry,
      matter: getDemoMatterById(entry.matterId),
    })),
    draftsInProgress: scope.draftsInProgress.map((entry) => ({
      ...entry,
      matter: getDemoMatterById(entry.matterId),
    })),
    recentExports: scope.recentExports.map((entry) => ({
      ...entry,
      matter: getDemoMatterById(entry.matterId),
    })),
  };
}

export function normalizeDemoPath(pathname) {
  const normalized = normalizePathname(pathname);

  if (ROUTE_ALIASES[normalized]) {
    return ROUTE_ALIASES[normalized];
  }

  if (normalized === DEFAULT_DEMO_ROUTE) {
    return DEFAULT_DEMO_ROUTE;
  }

  const match = normalized.match(/^\/demo\/matters\/([^/]+)(?:\/([^/]+))?$/);
  if (!match) {
    return DEFAULT_DEMO_ROUTE;
  }

  const [, matterId, sectionId] = match;
  if (!DEMO_MATTER_BY_ID.has(matterId)) {
    return DEFAULT_DEMO_ROUTE;
  }

  if (!sectionId || !VALID_SECTION_IDS.has(sectionId)) {
    return buildDemoMatterPath(matterId, DEFAULT_DEMO_SECTION_ID);
  }

  return buildDemoMatterPath(matterId, sectionId);
}

export function getDemoRouteState(pathname) {
  const canonicalPath = normalizeDemoPath(pathname);

  if (canonicalPath === DEFAULT_DEMO_ROUTE) {
    return {
      view: 'home',
      path: DEFAULT_DEMO_ROUTE,
      matter: null,
      section: null,
    };
  }

  const match = canonicalPath.match(/^\/demo\/matters\/([^/]+)\/([^/]+)$/);
  const matterId = match?.[1] || DEFAULT_DEMO_MATTER_ID;
  const sectionId = match?.[2] || DEFAULT_DEMO_SECTION_ID;

  return {
    view: 'matter',
    path: canonicalPath,
    matter: getDemoMatterById(matterId),
    section: DEMO_WORKSPACE_SECTIONS.find((section) => section.id === sectionId) || DEMO_WORKSPACE_SECTIONS[0],
  };
}
